import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { observer } from "mobx-react-lite";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useEditData } from "../../../hooks/useEditData";
import FormLayout from '../../Generic/FormLayout';
import FormInput from '../../Generic/FormInput';
import FormCheckbox from '../../Generic/FormCheckbox';
import { useSetMenu } from "../../../hooks/useSetMenu";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import * as yup from "yup";
import FormSelect from '../../Generic/FormSelect';
import { useSnackbar } from "notistack";
const Edit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { mainStore } = useStore();
    const axiosPrivate = useAxiosPrivate();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    useSetMenu(mainStore.CriteriaMenuType, mainStore.CriteriaMenuNo);
    const schema = yup.object({
        name: yup
            .string()
            .required(t('requiredField'))
            .max(100, t('maxLength', { max: 100 })),
        subcategoryId: yup.string().required(t("selectRequired")),
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        weightCoefficient: yup.string().required(t("selectRequired")),
    });

    const { register, handleSubmit, formState: { errors }, setValue } = useForm({ resolver: yupResolver(schema) });
    const onSubmit = data => updateData(data);

    const [panels, setPanels] = useState([]);
    const [selectedPanel, setSelectedPanel] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [subcategories, setSubcategories] = useState([]);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    const [weightCoefficients, setWeightCoefficients] = useState([
        { value: 1.0, label: '1.0' },
        { value: 0.5, label: '0.5' },
        { value: 0.33, label: '0.33' }
    ]);
    const [selectedWeightCoefficient, setSelectedWeightCoefficient] = useState(null);

    const additionalDataFetch = async (criteriaData) => {
        try {
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setPanels(panelsResponse.data);
            const selPanel = panelsResponse.data.find(
                (c) => c.value === criteriaData.panelId.toString()
            );
            setSelectedPanel(selPanel);

            let categoriesResponse;
            if (selPanel) {
                categoriesResponse = await axiosPrivate.get(
                    `/Categories/SelectOptionsByPanel/${selPanel.value}`
                );
                setCategories(categoriesResponse.data);
            }
            const selCategory = categoriesResponse.data.find(
                (c) => c.value === criteriaData.categoryId.toString()
            );
            setSelectedCategory(selCategory);

            let subcategoriesResponse;
            if (selCategory) {
                subcategoriesResponse = await axiosPrivate.get(
                    `/Subcategories/SelectOptionsByCategory/${selCategory.value}`
                );
                setSubcategories(subcategoriesResponse.data);
            }
            const selSubcategory = subcategoriesResponse.data.find(
                (c) => c.value === criteriaData.subcategoryId.toString()
            );
            setSelectedSubcategory(selSubcategory);

            const selectedWeightCoefficient = weightCoefficients.find(
                (c) => c.value === criteriaData.weightCoefficient
            );
            setSelectedWeightCoefficient(selectedWeightCoefficient);

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };


    const handleCategoryChange = async (selectedOption) => {
        setValue("categoryId", selectedOption.value, { shouldValidate: true });
        setSelectedCategory(selectedOption);
        try {
            const response = await axiosPrivate.get(
                `/Subcategories/SelectOptionsByCategory/${selectedOption.value}`
            );
            if (typeof response.data === "string" && response.data === "No records") {
                setSubcategories([]);
            } else {
                setSubcategories(response.data);
            }

            setSelectedSubcategory(null);
            setValue("subcategoryId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handleSubcategoryChange = selectedOption => {
        setSelectedSubcategory(selectedOption);
        setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
    };

    const handlePanelChange = async (selectedOption) => {
        setValue("panelId", selectedOption.value, { shouldValidate: true });
        setSelectedPanel(selectedOption);
        try {
            const response = await axiosPrivate.get(
                `/Categories/SelectOptionsByPanel/${selectedOption.value}`
            );
            if (typeof response.data === "string" && response.data === "No records") {
                setCategories([]);
            } else {
                setCategories(response.data);
            }
            setSubcategories([]);

            setSelectedSubcategory(null);
            setSelectedCategory(null);

            setValue("categoryId", null, { shouldValidate: true });
            setValue("subcategoryId", null, { shouldValidate: true });

        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };

    const handleWeightCoefficientChange = selectedOption => {
        setSelectedWeightCoefficient(selectedOption);
        setValue("weightCoefficient", selectedOption.value, { shouldValidate: true });
    };

    const { notFound, updateData } = useEditData(axiosPrivate, "/Criterias", id, setValue, navigate, additionalDataFetch);

    const goToList = () => {
        navigate("/Criteria/List");
    };

    if (notFound) {
        return <div className="not-found-myDiv">
            <div className="not-found-container">
                <div className="not-found-content">
                    <h1>{t("pageNotFoundTitle")}</h1>
                    <p>{t("noDataWithIdMessage")}</p>
                    <button onClick={goToList} className="btn btn-primary">
                        {t("goTo") + " " + t("list") + " " + t("criterias")}
                    </button>
                </div>
            </div>
        </div>;
    }

    return (
        <FormLayout title={t("criteriaEditTitle")} onSubmit={handleSubmit(onSubmit)} onCancel={() => navigate(-1)}>
            <FormSelect label="panel" name="panelId" register={register} value={selectedPanel} errors={errors} options={panels} onChange={handlePanelChange} />
            <FormSelect label="category" name="categoryId" register={register} errors={errors} options={categories} value={selectedCategory} onChange={handleCategoryChange} />
            <FormSelect label="subcategory" name="subcategoryId" options={subcategories} value={selectedSubcategory} onChange={handleSubcategoryChange} register={register} errors={errors}  />
            <FormInput label="criteria" name="name" register={register} errors={errors}  />
            <FormSelect label="criteriaWeightCoefficient" name="weightCoefficient" register={register} value={selectedWeightCoefficient} errors={errors} options={weightCoefficients} onChange={handleWeightCoefficientChange} />
            <FormCheckbox label="isMandatory" register={register} name="isMandatory" />
            <FormCheckbox label="isActive" register={register} name="isActive" />
        </FormLayout>
    );
};

export default observer(Edit);